import { createTheme } from '@abyss/web/tools/theme';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import React, { useContext, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { SmartChoiceWidget } from '../../common/Widget/SmartChoiceWidget';
import {
  PortalContext,
  PortalContextProvider,
} from '../../context/PortalContext';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { ProviderType } from '../../models/ProviderDetails';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { coerceBoolean } from '../../utils/string.utils';
import { Configuration } from '../ProviderSearch/context/Configuration';
import { DateTimeConfig } from '../ProviderSearch/context/DateTimeConfig';
import { Internationalization } from '../ProviderSearch/context/Internationalization';
import { Obapi } from '../ProviderSearch/context/Obapi';
import { getTheme } from '../themeOverrides/theme.utils';

/**
 * Props for SmartChoiceWidgetApp - uses kebab-case for HTML attribute compatibility
 */
interface SmartChoiceWidgetAppProps {
  breadcrumbs?: any[];
  'flex-css'?: string;
  'is-full-screen-compare-smart-choice'?: boolean | string;
  'is-mixed-results-page'?: boolean | string;
  'is-super-user'?: boolean | string;
  'show-details-page'?: boolean;
  'member-id'?: string;
  'portal-data'?: string;
  'show-choose-pcp-header'?: boolean | string;
  'show-member-selection'?: boolean | string;
  'submit-button-label'?: string;
  'claim-system-type-code'?: string;
  'coverage-type'?: string;
  'eligibility-system-type-code'?: string;
  latitude?: string;
  lob?: string;
  'location-id'?: string;
  longitude?: string;
  'plan-variation-code'?: string;
  'plan-year'?: string;
  'policy-id'?: string;
  'provider-id'?: string;
  'provider-type'?: string;
  'reciprocity-id'?: string;
  'membership-category'?: string;
  population?: string;
  'is-smart-choice-enabled'?: boolean;
  'client-name'?: string;
  'billing-codes'?: string;
  'tiered-plan'?: boolean;
  'recommendation-radius'?: string;
  'suppress-premium'?: string;
  'ces-product-code'?: string;
  'market-type'?: string;
}

export const SmartChoiceWidgetApp = (args: SmartChoiceWidgetAppProps = {}) => {
  const {
    breadcrumbs = [],
    'flex-css': flexCssJson,
    'is-full-screen-compare-smart-choice': isFullScreenCompareSmartChoice,
    'is-mixed-results-page': isMixedResultsPage,
    'is-super-user': isSuperUserRaw,
    'show-details-page': showDetailsPage,
    'member-id': memberId,
    'portal-data': portalDataJson,
    'show-choose-pcp-header': showChoosePCPHeader,
    'show-member-selection': showMemberSelection,
    'submit-button-label': submitButtonLabel,
    'claim-system-type-code': claimSystemTypeCode,
    'coverage-type': coverageType,
    'eligibility-system-type-code': eligibilitySystemTypeCode,
    latitude,
    lob,
    'location-id': locationId,
    longitude,
    'plan-variation-code': planVariationCode,
    'plan-year': planYear,
    'policy-id': policyId,
    'provider-id': providerId,
    'provider-type': providerTypeRaw,
    'reciprocity-id': reciprocityId,
    'membership-category': membershipCategory,
    population,
    'is-smart-choice-enabled': isSmartChoiceEnabledRaw,
    'client-name': clientName,
    'billing-codes': billingCodes,
    'recommendation-radius': recommendationRadius,
    'tiered-plan': tieredPlan,
    'suppress-premium': suppressPremium,
    'ces-product-code': cesProductCode,
    'market-type': marketType,
  } = args || {};

  // Convert HTML attributes to proper booleans
  const isSuperUser = coerceBoolean(isSuperUserRaw);
  const isFullScreen = coerceBoolean(isFullScreenCompareSmartChoice);
  const isMixedResults = coerceBoolean(isMixedResultsPage);
  const shouldShowChoosePCPHeader = coerceBoolean(showChoosePCPHeader);
  const shouldShowMemberSelection = coerceBoolean(showMemberSelection, true);
  const isSmartChoiceEnabled = coerceBoolean(isSmartChoiceEnabledRaw, true);
  const providerType =
    providerTypeRaw && providerTypeRaw in ProviderType
      ? ProviderType[providerTypeRaw]
      : ProviderType.PRACTITIONER;

  const setIsWidget = useStore(StoreKeys.SET_IS_WIDGET);
  const setMemberState = useStore(StoreKeys.SET_OBAPI_MEMBERS);

  const { setStoreCoverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      setStoreCoverageType: state.setCoverageType,
    }))
  );

  const portalData = {
    ...JSON.parse(portalDataJson || '{}'),
    clientName,
    recommendationRadius,
    tieredPlan,
    suppressPremium,
  };
  const flexCss = flexCssJson ? JSON.parse(flexCssJson) : undefined;
  const { setPortalData } = useContext(PortalContext);
  const isPortalWidget = !!portalData?.portalName;
  const isGEHAV2: boolean = shouldUseGehaV2Styling();

  const themeOverride = getTheme(
    portalData?.portalConfig?.theme,
    portalData?.portalIdentifier,
    portalData?.portalConfig?.themeOverride,
    isGEHAV2
  );

  const theme = createTheme(
    portalData?.portalConfig?.theme || 'uhc',
    themeOverride
  );

  useEffect(() => {
    setIsWidget(true);
    // Use the same default as the rendered widget to keep chip store consistent.
    setStoreCoverageType(coverageType || 'M');
  }, []);

  useEffect(() => {
    if (isPortalWidget) {
      setPortalData(portalData);
      const portalMemberInfo = portalData?.portalMemberInfo?.memberInfo;
      if (portalMemberInfo?.length) {
        setMemberState(portalMemberInfo);
      } else {
        // Build minimum member shape for acoBenefitPlan when portalMemberInfo is absent.
        // cesProductCode / marketType come from CEX widget attributes.
        const widgetMemberInfo = [
          {
            eligibility: [
              {
                memberHealthCoverage: {
                  coverageType: [
                    {
                      typeCode: { coverageTypeCode: coverageType || 'M' },
                      ...(cesProductCode
                        ? { productCode: cesProductCode }
                        : {}),
                      ...(marketType ? { marketType } : {}),
                    },
                  ],
                },
              },
            ],
            lineOfBusiness: lob,
            membershipCategory,
            policyNumber: policyId,
            population,
          },
        ];
        setMemberState(widgetMemberInfo);
      }
    }
    // Depend on stable primitive attrs instead of the portalData object reference
    // (which is reconstructed inline each render) to avoid re-seeding the member
    // store on every render and overwriting any subsequent member-selection updates.
  }, [
    isPortalWidget,
    portalDataJson,
    clientName,
    coverageType,
    cesProductCode,
    marketType,
    lob,
    membershipCategory,
    policyId,
    population,
  ]);

  if (isPortalWidget) {
    return (
      <PortalContextProvider portalDataArgs={portalData}>
        <Configuration isWidget>
          <DateTimeConfig>
            <Obapi isWidget>
              <Internationalization>
                <OverlayProvider>
                  <ThemeProvider theme={theme}>
                    <SmartChoiceWidget
                      billingCodes={billingCodes}
                      breadcrumbs={breadcrumbs}
                      claimSystemTypeCode={claimSystemTypeCode}
                      coverageType={coverageType || 'M'}
                      eligibilitySystemTypeCode={eligibilitySystemTypeCode}
                      flexCss={flexCss}
                      isFullScreenCompareSmartChoice={isFullScreen}
                      isMixedResultsPage={isMixedResults}
                      isParcelWidget={true}
                      isSmartChoiceEnabled={isSmartChoiceEnabled}
                      isSuperUser={isSuperUser}
                      latitude={latitude}
                      lob={lob}
                      locationId={locationId}
                      longitude={longitude}
                      memberId={memberId}
                      membershipCategory={membershipCategory}
                      planVariationCode={planVariationCode}
                      planYear={planYear}
                      policyId={policyId}
                      population={population}
                      providerId={providerId}
                      providerType={providerType}
                      reciprocityId={reciprocityId}
                      showChoosePCPHeader={shouldShowChoosePCPHeader}
                      showDetailsPage={showDetailsPage}
                      showMemberSelection={shouldShowMemberSelection}
                      submitButtonLabel={submitButtonLabel}
                    />
                  </ThemeProvider>
                </OverlayProvider>
              </Internationalization>
            </Obapi>
          </DateTimeConfig>
        </Configuration>
      </PortalContextProvider>
    );
  }

  return null;
};
